import { useState, useRef, MouseEvent } from 'react';
import { gql, useLazyQuery, useQuery, useSubscription } from '@apollo/client';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PermPhoneMsgIcon from "@mui/icons-material/PermPhoneMsg";
import {
  Box,
  AppBar,
  Toolbar,
  Drawer,
  IconButton,
  MenuItem,
  Menu,
  FormControl,
  TextField,
  Autocomplete,
  Button,
  Collapse,
  Tooltip,
  Badge,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import ListAlt from '@mui/icons-material/ListAlt';
import TaskIcon from '@mui/icons-material/Task';
import { useSelector } from 'react-redux';

import { FlexBetween } from '../../commonStyles';
import { selectProject } from '../../features/project/projectSlice';
import { selectUser } from '../../features/auth/authSlice';
import { useLogout } from '../../features/auth/authHooks';
import { withRouter } from '../../utils/WithRouter';
import { IProjectAccess } from '../../types/user';
import SideMenu from './SideMenu';
import storeyviewLogo from '../../assets/logos/sv_logo_green.png';
import { selectPortal } from '../../features/portal/portalSlice';
import { theme } from '../../theme';
import '../priceGrid/mediaPrint.css';
import { setMerges } from '../../features/merge/mergeSlice';
import { useAppDispatch } from '../../app/hooks';

const Header = (props: any) => {
  const { projectid } = useParams();
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const portal = useSelector(selectPortal);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [projectEl, setProjectEl] = useState<null | HTMLElement>(null);
  const userOpen = Boolean(anchorEl);
  const projectOpen = Boolean(projectEl);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<any>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [tasks, setTasks] = useState<number>(0);

  const logout = useLogout();
  const navigate = useNavigate();

  useSubscription(TASKSUBSCRIPTION, {
    variables: { user: user._id },
    onData: async ({ data: { data } }: any) => {
      // Remove unit if not HL or SHL
      setTasks(tasks + 1);
    },
  });

  useQuery(TASKCOUNT, {
    variables: { filter: { viewedBy: user._id } },
    onCompleted: (data) => {
      setTasks(data.taskCount);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [searchAll, { loading: searchLoading }] = useLazyQuery(SEARCHALL, {
    onCompleted: (data) => {
      setSearch(data.globalSearch);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = (e: any, value: string) => {
    setInputValue(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (value) {
      searchDebounceRef.current = setTimeout(() => {
        searchAll({ variables: { project: projectid, search: value } });
      }, 500);
    }
  };

  const handleSearchInput = (event: any, values: any) => {
    setInputValue('');
    setSearchOpen(false);

    if (values.hasOwnProperty('suite') && values.suite) {
      navigate(`/${projectid}/dashboard/${values._id}`);
    } else if (values.hasOwnProperty('realtor') && values.realtor) {
      navigate(`/${projectid}/dashboard/search/${values.realtor._id}`, {
        state: {
          type: values.hasOwnProperty('brokerage') && values.brokerage ? 'realtor' : 'purchaser',
          search: `${values.firstName} ${values.lastName}`,
        },
      });
    } else {
      navigate(`/${projectid}/dashboard/search/${values._id}`, {
        state: {
          type: values.hasOwnProperty('brokerage') && values.brokerage ? 'realtor' : 'purchaser',
          search: `${values.firstName} ${values.lastName}`,
        },
      });
    }
  };

  const optionLabel = (option: any) => {
    if (option.suite) {
      return `${option.suite}`;
    } else {
      return `${option.firstName} ${option.lastName} ${option.email} ${option.primaryPhone}`;
    }
  };

  const renderType = (option: any) => {
    if (option.brokerage) {
      return 'Realtor';
    } else if (option.suite) {
      return 'Suite';
    } else {
      return 'Purchaser';
    }
  };

  const handleProjectNavigate = async (id: string) => {
    setProjectEl(null);
    await storeDispatch(setMerges({ items: [], count: 0 }));
    await navigate(`/${id}/dashboard`);
  };

  return (
    <AppBar className="grow" position="static">
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: '#ffffff',
          color: theme.palette.primary.main,
          borderBottom: `3px solid ${theme.palette.secondary.main}`,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              sx={{
                mr: 1,
                alignSelf: 'center',
                color: theme.palette.primary.main,
              }}
              aria-label="open drawer"
              onClick={() => setOpen(true)}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ mr: 2, alignSelf: 'center' }}>
              <Link to={`/${projectid}/dashboard`}>
                <img src={storeyviewLogo} alt="storeyview" style={{ height: '22px', display: 'block' }} />
              </Link>
            </Box>
            {!showSearch ? (
              <Box
                sx={(theme) => ({
                  [theme.breakpoints.down('sm')]: {
                    display: 'none',
                  },
                  [theme.breakpoints.up('sm')]: {
                    display: 'block',
                  },
                  alignSelf: 'center',
                })}
              >
                <FormControl>
                  <Autocomplete
                    id="search"
                    open={searchOpen}
                    onOpen={() => {
                      if (inputValue) {
                        setSearchOpen(true);
                      }
                    }}
                    loading={searchLoading}
                    disableClearable
                    freeSolo={false}
                    options={search}
                    clearOnBlur={true}
                    getOptionLabel={(option) => optionLabel(option)}
                    value={''}
                    onChange={handleSearchInput}
                    inputValue={inputValue}
                    onClose={() => setSearchOpen(false)}
                    onInputChange={(e, value, reason) => {
                      if (reason === 'reset') {
                        setInputValue('');
                      } else {
                        handleSearch(e, value);
                      }
                      if (!value) {
                        setSearchOpen(false);
                      }
                    }}
                    renderOption={(props, option: any) => {
                      return (
                        <li {...props}>
                          <div>
                            {option.suite ? option.suite : `${option.firstName} ${option.lastName}`}
                            <Box
                              sx={{
                                fontSize: '10px',
                              }}
                            >
                              <div>{option.email}</div>
                              <div>{option.primaryPhone}</div>
                              <div>{renderType(option)}</div>
                            </Box>
                          </div>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{
                          width: '300px',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '20px',
                            backgroundColor: '#f5f5f4',
                            '& fieldset': { borderColor: '#e2e2e2' },
                            '&:hover fieldset': { borderColor: '#cfcfcf' },
                            '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                          },
                          '& .MuiAutocomplete-inputRoot': {
                            color: '#333',
                          },
                        }}
                        required={false}
                        {...params}
                        size="small"
                        placeholder="Search"
                        inputProps={{ ...params.inputProps, 'aria-label': 'Search' }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            ) : null}
          </Box>
        </Box>
        <FlexBetween>
          <Box
            sx={(theme) => ({
              [theme.breakpoints.down('sm')]: {
                display: 'block',
              },
              [theme.breakpoints.up('sm')]: {
                display: 'none',
              },
              mt: 1,
              mr: 1,
              alignSelf: 'center',
            })}
          >
            <SearchIcon sx={{ color: theme.palette.primary.main }} onClick={() => setShowSearch(!showSearch)} />
          </Box>
           {user.type !== 'Developer' && user.type !== 'Marketing' ? (
            <Box
              sx={(theme) => ({
                mt: 1,
                mr: 1,
                alignSelf: 'center',
              })}
            >
              <Link to={`/calls`}>
                <Tooltip title="Notifications">
                  <PermPhoneMsgIcon sx={{ color: theme.palette.primary.main }} />
                </Tooltip>
              </Link>
            </Box>
          ) : null}
          {user.type !== 'Developer' && user.type !== 'Marketing' ? (
            <Box
              sx={(theme) => ({
                mt: 1,
                mr: 1,
                alignSelf: 'center',
              })}
            >
              <Link to={`/admin-dashboard`}>
                <Tooltip title="Admin Dashboard">
                  <FolderRoundedIcon sx={{ color: theme.palette.primary.main }} />
                </Tooltip>
              </Link>
            </Box>
          ) : null}
          {user.type !== 'Developer' ? (
            <Box
              sx={(theme) => ({
                mt: 1,
                mr: 1,
                alignSelf: 'center',
                display: 'flex',
              })}
            >
              <Link to={`/tasks`}>
                <Tooltip title="Tasks">
                  <Badge
                    sx={{
                      color: `${portal.primaryColor ? portal.primaryColor : '#2e453d'}`,
                      pb: 0.3,
                      height: '100%',
                      '& .MuiBadge-badge': { fontSize: 11, height: 18, minWidth: 18, backgroundColor: 'yellow !important' },
                    }}
                    invisible={tasks > 0 ? false : true}
                    badgeContent={tasks}
                  >
                    <TaskIcon sx={{ color: theme.palette.primary.main }} />
                  </Badge>
                </Tooltip>
              </Link>
            </Box>
          ) : null}
          {user.type !== 'Developer' && user.type !== 'Admin' ? (
            <Box
              sx={(theme) => ({
                mt: 1,
                mr: 1,
                alignSelf: 'center',
              })}
            >
              <Tooltip title="Create Worksheet">
                <Link to={`/${project._id}/dashboard/create-worksheet`}>
                  <ListAlt sx={{ color: theme.palette.primary.main }} />
                </Link>
              </Tooltip>
            </Box>
          ) : null}
          {user.type !== 'Developer' && user.type !== 'Marketing' ? (
            <Box
              sx={(theme) => ({
                mt: 1,
                mr: 1,
                alignSelf: 'center',
              })}
            >
              <Tooltip title="Daily Walk ins">
                <a
                  style={{ color: theme.palette.primary.main }}
                  href={`https://crm.storeyview.com/dashboard/project/${
                    project._id
                  }/list/came-ins?row=15&page=1&cameInStart=${new Date().setHours(0, 0, 0, 0)}&cameInEnd=${new Date().getTime()}`}
                  rel="noreferrer"
                  target="_blank"
                >
                  <DirectionsWalkIcon sx={{ cursor: 'pointer' }} />
                </a>
              </Tooltip>
            </Box>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, mt: 1, mr: 1, alignSelf: 'center' }}>
            {project.name ? (
              <Button
                id="basic-button"
                aria-controls={projectOpen ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={projectOpen ? 'true' : undefined}
                onClick={(e: MouseEvent<HTMLButtonElement>) => setProjectEl(e.currentTarget)}
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  lineHeight: 1.3,
                  '&:hover': { backgroundColor: 'transparent' },
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '10px',
                  },
                }}
              >
                {project.name}
              </Button>
            ) : null}
            {project.name && user ? (
              <Box sx={{ width: '1px', height: '16px', backgroundColor: '#d8d8d8' }} />
            ) : null}
            {user ? (
              <Button
                id="basic-button"
                aria-controls={userOpen ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={userOpen ? 'true' : undefined}
                onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
                sx={{
                  color: '#8a8a8a',
                  fontWeight: 400,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  lineHeight: 1.3,
                  '&:hover': { backgroundColor: 'transparent' },
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '10px',
                  },
                }}
              >
                {user.fullName}
              </Button>
            ) : null}
          </Box>
        </FlexBetween>
        <Menu
          id="basic-menu"
          anchorEl={projectEl}
          open={projectOpen}
          onClose={() => setProjectEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {[...user?.projectAccess]
            .sort((a: any, b: any) => a.project.name.localeCompare(b.project.name))
            .map((project: IProjectAccess, index: number) => {
              return (
                <MenuItem key={index} onClick={() => handleProjectNavigate(project.project?._id!)}>
                  {project.project.name}
                </MenuItem>
              );
            })}
        </Menu>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={userOpen}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              navigate('/change-password');
            }}
          >
            Change Password
          </MenuItem>
          <MenuItem onClick={() => logout()}>Logout</MenuItem>
        </Menu>
        <Drawer
          anchor="left"
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            paper: {
              width: 250,
              // [theme.breakpoints.down('sm')]: {
              //   width: '100%',
              // },
            },
          }}
        >
          <SideMenu handleClose={() => setOpen(false)} />
        </Drawer>
      </Toolbar>
      {showSearch ? (
        <Collapse
          in={showSearch}
          easing={'enter'}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              display: 'block',
            },
            [theme.breakpoints.up('sm')]: {
              display: 'none',
            },
            textAlign: 'center',
            pb: 2,
            backgroundColor: '#ffffff',
          })}
        >
          <FormControl>
            <Autocomplete
              id="search-mobile"
              open={searchOpen}
              onOpen={() => {
                if (inputValue) {
                  setSearchOpen(true);
                }
              }}
              loading={searchLoading}
              disableClearable
              freeSolo={false}
              options={search}
              clearOnBlur={true}
              getOptionLabel={(option) => optionLabel(option)}
              value={''}
              onChange={handleSearchInput}
              inputValue={inputValue}
              onClose={() => setSearchOpen(false)}
              onInputChange={(e, value, reason) => {
                if (reason === 'reset') {
                  setInputValue('');
                } else {
                  handleSearch(e, value);
                }
                if (!value) {
                  setSearchOpen(false);
                }
              }}
              renderOption={(props, option: any) => {
                return (
                  <li {...props}>
                    <div>
                      {option.suite ? option.suite : `${option.firstName} ${option.lastName}`}
                      <Box
                        sx={{
                          fontSize: '10px',
                        }}
                      >
                        <div>{option.email}</div>
                        <div>{option.primaryPhone}</div>
                        <div>{renderType(option)}</div>
                      </Box>
                    </div>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{
                    width: '350px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '20px',
                      backgroundColor: '#f5f5f4',
                      '& fieldset': { borderColor: '#e2e2e2' },
                      '&:hover fieldset': { borderColor: '#cfcfcf' },
                      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
                    },
                    '& .MuiAutocomplete-inputRoot': {
                      color: '#333',
                    },
                  }}
                  required={false}
                  {...params}
                  size="small"
                  placeholder="Search"
                  inputProps={{ ...params.inputProps, 'aria-label': 'Search' }}
                />
              )}
            />
          </FormControl>
        </Collapse>
      ) : null}
    </AppBar>
  );
};

const SEARCHALL = gql`
  query globalSearch($search: String!, $project: MongoID!) {
    globalSearch(search: $search, project: $project) {
      _id
      suite
      firstName
      lastName
      primaryPhone
      email
      brokerage
      realtor {
        _id
      }
    }
  }
`;

const TASKCOUNT = gql`
  query taskCount($filter: FilterCountTaskInput) {
    taskCount(filter: $filter)
  }
`;

const TASKSUBSCRIPTION = gql`
  subscription logTask($user: String!) {
    logTask(user: $user) {
      _id
    }
  }
`;

export default withRouter(Header);
